/**
 * iter150AZ.80 — ScheduleTimeline: a small horizontal 24-hour bar
 * visualising the stop windows of one weekday.
 *
 * Anatomy (left → right):
 *
 *     0 ─── 6 ─── 12 ─── 18 ─── 24
 *     ░░░░░██░░░░░░░░░░░████░░░░░    ← rose-300 segments = stop windows
 *
 *   • Pale-slate base background represents "running 24/7" (the default).
 *   • Rose-300 segments mark the stop windows. Hover shows the
 *     "HH:MM - HH:MM" tooltip.
 *   • Tick marks every 6 hours plus 12-noon a darker grey.
 *   • If two windows overlap (which shouldn't happen post-iter150AZ.79
 *     thanks to the picker, but legacy data may have it) the overlap
 *     region renders rose-700 so it stands out for cleanup.
 */
import { useEffect, useState } from "react";
import { _toMinutes, windowsOverlap } from "../../pages/sequencing/scheduleOverlap";

const DAY_MIN = 24 * 60;

function _clamp(x, lo, hi) { return Math.max(lo, Math.min(hi, x)); }

/** Returns minutes-since-midnight for the current local time.
 *  Module-level helper (not a hook) so server-side render passes
 *  produce deterministic markup using whatever Date.now returns. */
function _nowMinutes() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

export default function ScheduleTimeline({ windows, testId, isToday = false }) {
  const list = Array.isArray(windows) ? windows : [];

  // "Now" indicator — tick that re-renders every 30s so the line
  // walks across the bar as the day progresses. Initial value is
  // captured eagerly so the very first paint already shows it. Only
  // ever rendered when ``isToday`` is true (otherwise it would be
  // misleading on the other six rows).
  const [nowMin, setNowMin] = useState(_nowMinutes);
  useEffect(() => {
    if (!isToday) return undefined;
    const handle = setInterval(() => setNowMin(_nowMinutes()), 30 * 1000);
    return () => clearInterval(handle);
  }, [isToday]);
  const nowPct = (nowMin / DAY_MIN) * 100;

  // Convert each window to a {leftPct, widthPct, label} segment.
  const segments = list
    .map((w) => {
      const s = _toMinutes(w?.start_time);
      const e = _toMinutes(w?.end_time);
      if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return null;
      const left  = _clamp(s, 0, DAY_MIN) / DAY_MIN * 100;
      const width = _clamp(e - s, 0, DAY_MIN) / DAY_MIN * 100;
      return {
        leftPct: left,
        widthPct: width,
        label: `${w.start_time}–${w.end_time}`,
        start: s,
        end: e,
        raw: w,
      };
    })
    .filter(Boolean);

  // Detect overlapping pairs so we can deepen their colour in the
  // visualisation — purely informational, the picker already prevents
  // new overlaps from being created.
  const overlapIdx = new Set();
  for (let i = 0; i < segments.length; i++) {
    for (let j = i + 1; j < segments.length; j++) {
      if (windowsOverlap(segments[i].raw, segments[j].raw)) {
        overlapIdx.add(i); overlapIdx.add(j);
      }
    }
  }

  return (
    <div data-testid={testId}
         className="select-none"
         aria-label="24-hour stop windows preview">
      {/* Bar */}
      <div className="relative h-3 bg-slate-100 rounded-sm border border-slate-200 overflow-hidden">
        {/* Tick marks every 6h. */}
        {[6, 12, 18].map((h) => (
          <div key={h}
               aria-hidden
               className={`absolute top-0 bottom-0 w-px ${h === 12 ? "bg-slate-300" : "bg-slate-200"}`}
               style={{ left: `${(h / 24) * 100}%` }} />
        ))}
        {/* Stop-window segments. */}
        {segments.map((seg, i) => (
          <div key={i}
               data-testid={testId ? `${testId}-seg-${i}` : undefined}
               title={seg.label}
               aria-label={`Stop window ${seg.label}`}
               className={`absolute top-0 bottom-0 ${
                 overlapIdx.has(i)
                   ? "bg-rose-600/80 ring-1 ring-rose-700"
                   : "bg-rose-400/80 hover:bg-rose-500/90"
               }`}
               style={{
                 left:  `${seg.leftPct}%`,
                 width: `${Math.max(seg.widthPct, 0.5)}%`,    // ensure 1px minimum
               }} />
        ))}
        {/* iter150AZ.81 — "now" indicator. Thin amber vertical line at
            the current local time so operators can read at a glance
            whether they're inside a stop window right now. Rendered
            only on today's row to avoid suggesting every day is "now". */}
        {isToday && (
          <div data-testid={testId ? `${testId}-now` : undefined}
               aria-label="Current time"
               title={`Now: ${String(Math.floor(nowMin / 60)).padStart(2, "0")}:${String(nowMin % 60).padStart(2, "0")}`}
               className="absolute top-[-2px] bottom-[-2px] w-[2px] bg-amber-500 shadow-[0_0_0_1px_white] z-10 pointer-events-none"
               style={{ left: `${nowPct}%` }} />
        )}
      </div>
      {/* Hour labels */}
      <div className="relative h-3 mt-0.5 text-[9px] text-slate-400 font-mono">
        {[0, 6, 12, 18, 24].map((h) => (
          <span key={h}
                aria-hidden
                className="absolute -translate-x-1/2"
                style={{
                  left: `${(h / 24) * 100}%`,
                  // Edge labels (0 & 24) hug their respective sides so
                  // they don't overflow the bar's bounding box.
                  ...(h === 0  ? { left: 0,     transform: "translateX(0)" }   : {}),
                  ...(h === 24 ? { left: "100%", transform: "translateX(-100%)" } : {}),
                }}>
            {h === 24 ? "24" : `${h}`}
          </span>
        ))}
      </div>
    </div>
  );
}
